// –———————————————————————————————————————————— LIB. –———————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import { gsap } from "gsap";

// ———————————————————————————————————————————— UTIL. ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

import valid_str from "../../../../../app/baseUtilities/validate/validate_string";

/**
 * Hide all portraits immediately without delay
 */
function hideAllPortraitsImmediate() {
  const allPortraits = document.querySelectorAll('[data-ref="previewPortrait"]');
  allPortraits.forEach((portrait) => {
    gsap.killTweensOf(portrait); // Kill any ongoing animations
    gsap.set(portrait, { opacity: 0 }); // Immediate hide
  });
}

/**
 * Hide all portraits except the specified array
 */
function hideAllPortraitsExcept(exceptPortraits = []) {
  const allPortraits = document.querySelectorAll('[data-ref="previewPortrait"]');
  allPortraits.forEach((p) => {
    if (!exceptPortraits.includes(p)) {
      gsap.killTweensOf(p);
      gsap.set(p, { opacity: 0 });
    }
  });
}

// ———————————————————————————————————————————— SETUP ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

// Configuration
const CONFIG = {
  PORTRAITS_SHOW_DELAY: 130,
  PORTRAITS_HIDE_DELAY: 0, // Immediate hiding to prevent race conditions
  MOUSE_OFFSET_X: 0,
  MOUSE_OFFSET_Y: 0,
  ANIM_DURATION: 0.25,
  FOLLOW_DURATION: 0.25, // Increased for more drag effect
  PORTRAIT_CIRCLE_RADIUS: 200, // Distance from mouse center to portraits
};

/**
 * Global state management for portraits
 */
let activePortraits = [];
let activeItemId = null;
let activeHeader = null;
let isHoveringHeader = false;
let showPortraitsTimeout = null;
let hidePortraitsTimeout = null;
let globalCleanupTimeout = null;

// ————————————————————————————————–———————————— fn() ————————————————————————————————————————————— //
// ———————————————————————————————————————————————————————————————————————————————————————————————— //

/**
 * Helper function to clear all active timeouts
 */
function clearAllTimeouts() {
  if (showPortraitsTimeout) {
    clearTimeout(showPortraitsTimeout);
    showPortraitsTimeout = null;
  }
  if (hidePortraitsTimeout) {
    clearTimeout(hidePortraitsTimeout);
    hidePortraitsTimeout = null;
  }
  if (globalCleanupTimeout) {
    clearTimeout(globalCleanupTimeout);
    globalCleanupTimeout = null;
  }
}

/**
 * Force hide all portraits and reset state
 */
function forceHideAllPortraits() {
  hideAllPortraitsImmediate();
  activePortraits = [];
  activeItemId = null;
  activeHeader = null;
  isHoveringHeader = false;
  clearAllTimeouts();
}

/**
 * Reset state for specific item only
 */
function resetStateForItem(header, itemId) {
  if (activeHeader === header && activeItemId === itemId) {
    activePortraits = [];
    activeItemId = null;
    activeHeader = null;
    isHoveringHeader = false;
  }
}

/**
 * Calculate circular position with randomization for a portrait
 * @param {number} index - Portrait index in the array
 * @param {number} totalPortraits - Total number of portraits
 * @param {number} radius - Base circle radius
 * @returns {object} Object with x and y offset from center
 */
function calc_portraitPos_circular(index, totalPortraits, radius) {
  // Calculate evenly spaced circular position
  const angle = (index / totalPortraits) * Math.PI * 2; // Evenly divide circle
  const randomAngleOffset = (Math.random() - 0.5) * (Math.PI / totalPortraits) * 1.5;
  const angle_withOffset = angle + randomAngleOffset;

  const dist = radius;
  const randomDistOffset = (Math.random() - 0.5) * 80;
  const dist_withOffset = dist + randomDistOffset;

  return {
    x: Math.cos(angle_withOffset) * dist_withOffset,
    y: Math.sin(angle_withOffset) * dist_withOffset,
  };
}

/**
 * Global mouse move handler for portrait following
 */
export function hdl_global_mouseMove(e) {
  // Guard //
  if (!activePortraits || activePortraits.length === 0 || !isHoveringHeader) return;

  // Setup //
  const mouseX = e.clientX + CONFIG.MOUSE_OFFSET_X;
  const mouseY = e.clientY + CONFIG.MOUSE_OFFSET_Y;

  // Follow animation with maintained circular offsets
  activePortraits.forEach((p) => {
    // Apply the consistent circular offset to the current mouse position
    // (offsets are set during mouseEnter for circular positioning)
    if (p._randomOffset) {
      const targetX = mouseX + p._randomOffset.x;
      const targetY = mouseY + p._randomOffset.y;

      gsap.to(p, {
        left: targetX,
        top: targetY,
        duration: CONFIG.FOLLOW_DURATION,
        ease: "power1.out", // Smoother, more linear easing for drag effect
      });
    }
  });
}

////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////

/**
 * Handle mouse enter on header - position and show portraits
 */
export function hdl_header_mouseEnter(e, id_comp) {
  // Guard: only show portraits on screens larger than mobile (550px)
  if (window.innerWidth <= 550) return;

  ////////////// Setup ///////////////
  ////////////////////////////////////

  const header = e.currentTarget;
  const item = header.closest('[g-ref="items"]');
  const id_item = item?.dataset.id;
  if (!valid_str(id_item) || item?.dataset.open === "true") return;
  const previewPortraits = document.querySelectorAll('[data-ref="previewPortrait"][data-id-comp="' + id_comp + '"][data-id-partner="' + id_item + '"]');
  const has_portraits = previewPortraits && previewPortraits.length > 0;
  if (!has_portraits) return;

  // Convert NodeList to Array for easier handling
  const portraitsArray = Array.from(previewPortraits);

  // Shuffle portraits array to randomize positioning order each time
  for (let i = portraitsArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [portraitsArray[i], portraitsArray[j]] = [portraitsArray[j], portraitsArray[i]];
  }

  ///////// Timeout cleanup //////////
  ////////////////////////////////////

  clearAllTimeouts(); // Clear all timeouts first
  hideAllPortraitsExcept(portraitsArray); // Hide OTHER portraits immediately (but not these ones)

  /////////// States updt. ///////////
  ////////////////////////////////////

  // Set active states immediately
  activePortraits = portraitsArray;
  activeItemId = id_item;
  activeHeader = header;
  isHoveringHeader = true;

  ////// Portrait pos. + reveal //////
  ////////////////////////////////////

  // Kill any ongoing animations on portraits and position them
  const mouseX = e.clientX + CONFIG.MOUSE_OFFSET_X;
  const mouseY = e.clientY + CONFIG.MOUSE_OFFSET_Y;

  portraitsArray.forEach((p, i) => {
    gsap.killTweensOf(p);

    // Calculate circular position around mouse
    p._randomOffset = calc_portraitPos_circular(i, portraitsArray.length, CONFIG.PORTRAIT_CIRCLE_RADIUS);

    // Updt. position
    const circularX = mouseX + p._randomOffset.x;
    const circularY = mouseY + p._randomOffset.y;
    gsap.set(p, {
      position: "fixed",
      left: circularX,
      top: circularY,
      zIndex: 999992 + i,
      opacity: 0,
      pointerEvents: "none",
      transform: "translate(-50%, -50%)", // Center the portrait on the calculated position
    });
  });

  // Show portraits after delay - with stricter validation
  showPortraitsTimeout = setTimeout(() => {
    // Triple-check state is still valid and we're still hovering this exact header
    if (activePortraits.length > 0 && activeItemId === id_item && activeHeader === header && isHoveringHeader) {
      activePortraits.forEach((p, index) => {
        const staggerDelay = index * 0.05; // 80ms delay between each portrait
        gsap.to(p, {
          opacity: 1,
          duration: CONFIG.ANIM_DURATION,
          ease: "back.out(1.2)",
          delay: staggerDelay,
        });
      });
    }
    showPortraitsTimeout = null;
  }, CONFIG.PORTRAITS_SHOW_DELAY);
}

////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////

/**
 * Handle mouse leave on header - hide portraits immediately
 */
export function hdl_header_mouseLeave(event) {
  // Guard: only show portraits on screens larger than mobile (550px)
  if (window.innerWidth <= 550) return;

  const header = event.currentTarget;
  const item = header.closest('[g-ref="items"]');
  const itemId = item?.dataset.id;
  if (!valid_str(itemId)) return;

  // Only process if this is the currently active header
  if (activeHeader !== header || activeItemId !== itemId) return;

  // Clear any pending show timeout immediately
  clearAllTimeouts();

  // Mark as no longer hovering this header
  isHoveringHeader = false;

  // IMMEDIATE hide for all active portraits - no delay
  if (activePortraits.length > 0) {
    activePortraits.forEach((p) => {
      gsap.killTweensOf(p);
      gsap.to(p, {
        opacity: 0,
        duration: CONFIG.ANIM_DURATION * 0.5, // Faster hide
        ease: "power2.in",
      });
    });

    // Reset state after a short delay to ensure animations complete
    setTimeout(() => {
      resetStateForItem(header, itemId);
    }, CONFIG.ANIM_DURATION * 0.5 * 1000 + 10);
  }
}

/**
 * Global document mouseleave handler to catch edge cases
 */
export function hdl_document_mouseLeave() {
  // Immediate cleanup when mouse leaves document
  forceHideAllPortraits();
}
